import React, { useEffect, useRef, useState } from 'react';
import BottomSheet from '../BottomSheet/BottomSheet';
import { useLanguage } from '../../../contexts/LanguageContext';
import { hapticFeedback } from '../../../utils/telegram';
import './ExchangeConnectionModal.css';

const ExchangeConnectionModal = ({ isOpen, onClose, onConnect }) => {
  const { t } = useLanguage();
  const [selectedExchange, setSelectedExchange] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [privateKey, setPrivateKey] = useState('');
  const [secretWord, setSecretWord] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');

  const exchanges = [
    {
      id: 'bitget',
      name: 'Bitget',
      requiresSecret: true,
      isLock: true,
      docUrl: 'https://bitgetlimited.github.io/apidoc/en/mix/#get-account-list'
    },
    {
      id: 'bingx',
      name: 'BingX',
      requiresSecret: false,
      isLock: false,
      docUrl: 'https://bingx-api.github.io/docs/#/en-us/common/account.html#Generate%20API%20Key'
    },
    {
      id: 'bybit',
      name: 'Bybit',
      requiresSecret: false,
      isLock: false,
      docUrl: 'https://www.bybit.com/en/help-center/article/How-to-create-your-API-key'
    }
  ];

  const handleExchangeSelect = (exchangeId) => {
    const exchange = exchanges.find(ex => ex.id === exchangeId);
    if (exchange?.isLock) {
      hapticFeedback.error();
      return;
    }
    hapticFeedback.light();
    setSelectedExchange(exchangeId);
    setError('');
  };

  const handleConnect = async () => {
    if (!selectedExchange || !apiKey || !privateKey) {
      setError(t('fillAllFields'));
      hapticFeedback.error();
      return;
    }

    const selectedExchangeData = exchanges.find(ex => ex.id === selectedExchange);
    if (selectedExchangeData?.requiresSecret && !secretWord) {
      setError(t('secretWordRequired'));
      hapticFeedback.error();
      return;
    }

    setIsConnecting(true);
    setError('');

    try {
      hapticFeedback.medium();
      await onConnect({
        exchangeId: selectedExchange,
        apiKey,
        privateKey,
        secretWord: selectedExchangeData?.requiresSecret ? secretWord : undefined
      });

      hapticFeedback.success();

      // Reset form
      setSelectedExchange('');
      setApiKey('');
      setPrivateKey('');
      setSecretWord('');
      onClose();
    } catch (error) {
      console.error('Exchange connection error:', error);
      setError(error?.response?.data?.error || error.message || t('connectionFailed'));
      hapticFeedback.error();
    } finally {
      setIsConnecting(false);
    }
  };

  const handleClose = () => {
    hapticFeedback.light();
    setSelectedExchange('');
    setApiKey('');
    setPrivateKey('');
    setSecretWord('');
    setError('');
    onClose();
  };

  const handleOpenDocumentation = () => {
    const exchange = exchanges.find(ex => ex.id === selectedExchange);
    if (exchange?.docUrl) {
      hapticFeedback.light();
      window.open(exchange.docUrl, '_blank');
    }
  };

  const selectedExchangeData = exchanges.find(ex => ex.id === selectedExchange);

  // Improve iOS keyboard UX specifically for the inputs inside this modal
  const containerRef = useRef(null);
  useEffect(() => {
    if (!isOpen) return;
    const el = containerRef.current;
    if (!el) return;
    const onFocus = (e) => {
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') {
        setTimeout(() => {
          try {
            t.scrollIntoView({ block: 'center', behavior: 'smooth' });
          } catch (_) {
            // noop
          }
        }, 250);
      }
    };
    el.addEventListener('focusin', onFocus);
    return () => el.removeEventListener('focusin', onFocus);
  }, [isOpen]);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title={t('connectExchange')}
      maxHeight="85vh"
    >
      <div ref={containerRef} className="exchange-connection-form">
        <div className="form-section">
          <label className="form-label">{t('selectExchange')}</label>
          <div className="exchange-options">
            {exchanges.map(exchange => (
              <div
                key={exchange.id}
                className={`exchange-option ${selectedExchange === exchange.id ? 'selected' : ''} ${exchange.isLock ? 'locked' : ''}`}
                onClick={() => handleExchangeSelect(exchange.id)}
              >
                <div className="exchange-name">{exchange.name}</div>
                {exchange.isLock && (
                  <div className="exchange-lock-indicator">Soon</div>
                )}
                {selectedExchange === exchange.id && !exchange.isLock && (
                  <div className="exchange-selected-indicator">✓</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {selectedExchange && (
          <div className="form-section">
            <div className="form-group">
              <label className="form-label" htmlFor="apiKey">{t('apiKey')}</label>
              <input
                id="apiKey"
                type="text"
                className="form-inputss"
                placeholder={t('enterApiKey')}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="privateKey">{t('privateKey')}</label>
              <input
                id="privateKey"
                type="password"
                className="form-inputss"
                placeholder={t('enterPrivateKey')}
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
              />
            </div>

            {selectedExchangeData?.requiresSecret && (
              <div className="form-group">
                <label className="form-label" htmlFor="secretWord">{t('secretWord')}</label>
                <input
                  id="secretWord"
                  type="password"
                  className="form-inputss"
                  placeholder={t('enterSecretWord')}
                  value={secretWord}
                  onChange={(e) => setSecretWord(e.target.value)}
                />
              </div>
            )}

            <div className="documentation-section">
              <button
                type="button"
                className="documentation-button"
                onClick={handleOpenDocumentation}
              >
                📚 {t('howToGetApiKey')}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message-box">
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            style={{
              display: selectedExchange ? 'block' : 'none'
            }}
            className="button secondary"
            onClick={handleClose}
          >
            {t('cancel')}
          </button>
          <button
            className="button primary"
            onClick={handleConnect}
            disabled={isConnecting || !selectedExchange || !apiKey || !privateKey}
          >
            {isConnecting ? t('connecting') : t('connect')}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default ExchangeConnectionModal; 